import {
    EventEmitter,
    Component,
    ElementRef,
    Input,
    NgZone,
    Output,
    ViewEncapsulation,
    AfterViewInit,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { WellHealthinessService } from 'app/modules/well-healthiness/services/well-healthiness.service';
import { HealthinessSummaryResponse } from 'app/modules/well-healthiness/model';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import module from 'app/modules/well-healthiness/well-healthiness.module';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'app-well-healthiness-chart',
    templateUrl: './healthiness-chart.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./healthiness-chart.component.scss'],
})
export class HealthinessChartComponent implements AfterViewInit, OnChanges {
    @Input() data: HealthinessSummaryResponse;
    @Input() activeCategory: string | null = null;
    @Output() categorySelected = new EventEmitter<string>();
    private chartCreated = false;
    private chartInstance: am4charts.PieChart | null = null;
    private pieSeriesInstance: am4charts.PieSeries | null = null;

    constructor(
        private readonly elementRef: ElementRef,
        private readonly ngZone: NgZone,
        public service: WellHealthinessService,
    ) {}

    ngAfterViewInit(): void {
        if (this.data && !this.chartCreated) {
            this.createHealthiniessPercentagePie();
            this.chartCreated = true;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['activeCategory'] &&
            !changes['activeCategory'].firstChange &&
            this.chartInstance
        ) {
            this.applyActiveCategorySlice();
        }

        if (changes['data'] && this.data) {
            // Use setTimeout to ensure view init has completed
            setTimeout(() => {
                this.createHealthiniessPercentagePie();
                this.chartCreated = true;
            }, 0);
        }
    }

    private getColorByCategory(category: string): string {
        const normalized = category.toLowerCase().trim();
        
        // Unhealthy states (checked first since 'unhealthy' contains 'healthy')
        if (normalized.includes('very high risk')) return '#991b1b';
        if (normalized.includes('high risk')) return '#b91c1c';
        if (normalized === 'unhealthy with moc') return '#ffd966'; // Orange-Yellow
        if (normalized === 'unhealthy') return '#ef4444'; // Red

        // Healthy states
        if (normalized === 'healthy with moc') return '#ffff00'; // Yellow
        if (normalized === 'healthy') return '#7ed77e'; // Green

        return '#7ed77e'; // Fallback
    }

    // Method removed

    private formatCategoryLabel(category: string): string {
        // Remove 'with' from category labels for display
        return category.replaceAll(/\bwith\b\s*/g, '');
    }

    private createHealthiniessPercentagePie() {
        if (!this.data?.items?.length) {
            return;
        }

        if (this.chartInstance) {
            this.chartInstance.dispose();
            this.chartInstance = null;
        }

        const chart = am4core.create('chartdiv', am4charts.PieChart);
        this.chartInstance = chart;
        chart.padding(0, 0, 0, 0);
        chart.width = am4core.percent(100);
        chart.height = am4core.percent(100);
        chart.align = 'center';
        chart.valign = 'middle';

        const allowedChartCategories = new Set([
            'healthy',
            'unhealthy',
            'healthy with moc',
            'unhealthy with moc'
        ]);

        const orderMap: Record<string, number> = {
            'healthy': 1,
            'healthy with moc': 2,
            'unhealthy': 3,
            'unhealthy with moc': 4
        };

        chart.data = [...this.data.items]
            .filter((item) => item.count > 0 && allowedChartCategories.has(item.category.toLowerCase()))
            .sort((a, b) => {
                const orderA = orderMap[a.category.toLowerCase()] || 99;
                const orderB = orderMap[b.category.toLowerCase()] || 99;
                return orderA - orderB;
            })
            .map((item) => ({
                category: this.formatCategoryLabel(item.category),
                originalCategory: item.category,
                value: item.count,
                color: am4core.color(this.getColorByCategory(item.category)),
            }));

        const pieSeries = chart.series.push(new am4charts.PieSeries());
        this.pieSeriesInstance = pieSeries;
        pieSeries.dataFields.value = 'value';
        pieSeries.dataFields.category = 'category';
        pieSeries.slices.template.propertyFields.fill = 'color';

        // Hover scales slice slightly; clicked slice keeps the same scale.
        pieSeries.slices.template.states.getKey('hover').properties.scale =
            1.04;
        pieSeries.slices.template.states.getKey(
            'hover',
        ).properties.shiftRadius = 0.01;
        pieSeries.slices.template.states.getKey('active').properties.scale =
            1.04;
        pieSeries.slices.template.states.getKey(
            'active',
        ).properties.shiftRadius = 0.01;

        // Base stroke; active slice will get stronger stroke in applyActiveCategorySlice.
        pieSeries.slices.template.stroke = am4core.color('#ffffff');
        pieSeries.slices.template.strokeWidth = 1;
        pieSeries.slices.template.strokeOpacity = 1;
        pieSeries.slices.template.cursorOverStyle =
            am4core.MouseCursorStyle.pointer;
        pieSeries.labels.template.disabled = true;
        pieSeries.ticks.template.disabled = true;

        pieSeries.slices.template.events.on('hit', (event) => {
            const category = (event.target?.dataItem?.dataContext as any)
                ?.originalCategory;

            if (category) {
                this.activeCategory = category;
                this.applyActiveCategorySlice();
                this.ngZone.run(() => {
                    this.categorySelected.emit(category);
                });
            }
        });

        pieSeries.events.on('validated', () => {
            this.applyActiveCategorySlice();
        });

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.contentAlign = 'center';
        chart.legend.labels.template.fill = am4core.color('#000000');
        chart.legend.labels.template.fontSize = 12;
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;
        chart.legend.valueLabels.template.fill = am4core.color('#000000');
        chart.legend.valueLabels.template.fontSize = 12;
        chart.legend.valueLabels.template.text =
            "{value} ({value.percent.formatNumber('#.#')}%)";

        chart.legend.markers.template.width = 12;
        chart.legend.markers.template.height = 12;
        const markerTemplate = chart.legend.markers.template.children.getIndex(
            0,
        ) as am4core.RoundedRectangle;
        markerTemplate.cornerRadius(4, 4, 4, 4);

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    private applyActiveCategorySlice(): void {
        if (!this.pieSeriesInstance) {
            return;
        }

        this.pieSeriesInstance.slices.each((slice) => {
            const category = (slice?.dataItem?.dataContext as any)
                ?.originalCategory;
            const isSelected = Boolean(
                this.activeCategory && category === this.activeCategory,
            );

            slice.isActive = isSelected;
            slice.strokeWidth = isSelected ? 3 : 1;
        });
    }
}
